import React, { useState, useEffect, useRef } from "react";
import TpRectangleCard from "../TpRectangleCard";
import { ContentItem } from "../../../../utils/contentMappers";
import { bookmarkContent, getLikeBookmarkedContent } from "../../service";
import { likeContent } from "../../service";
import useStore from "../../../../store/useStore";
import { getContentCounts } from "../../service";
import {
  getDecodedToken,
  getLocalStorage,
} from "../../../../utils/localStorage";
import { AUTH_TOKEN } from "../../../../utils/constants";

interface LatestSectionProps {
  items: ContentItem[];
  loading: boolean;
  showEmptyState?: boolean;
}

// Update the interface for content counts
interface ContentCounts {
  [key: string]: {
    isExternalSharable: number;
    like_count: number;
    uploadThumbnailUrl: string;
    previewUrl: string;
    readTime: string;
    duration: string;
  };
}

const LatestSection: React.FC<LatestSectionProps> = ({ items, loading, showEmptyState = true }) => {
  const [localLikes, setLocalLikes] = useState<number[]>([]);
  const [localBookmarks, setLocalBookmarks] = useState<number[]>([]);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [contentCounts, setContentCounts] = useState<ContentCounts>({});

  // Add ref for tracking last fetch
  const lastFetchRef = useRef<{
    time: number;
    contentIds: string[];
  }>({
    time: 0,
    contentIds: [],
  });

  const {
    tatvaPediaToken,
    tatvaPediaBookmarks,
    updateTatvaPediaBookmark,
    updateTatvaPediaLike,
    setTatvaPediaLikedList,
  } = useStore();

  // Add a ref to track pending operations
  const pendingOperations = useRef<{ [key: string]: boolean }>({});

  const { doctorDetails, setDoctorDetails } = useStore((state) => ({
    doctorDetails: state.doctorDetails,
    setDoctorDetails: state.setDoctorDetails,
  }));

  useEffect(() => {
    if (getLocalStorage(AUTH_TOKEN)) {
      const decodedToken = getDecodedToken();
      setDoctorDetails({ ...doctorDetails, ...decodedToken });
    }
  }, []);

  useEffect(() => {
    if (
      !tatvaPediaBookmarks?.likedList ||
      !Array.isArray(tatvaPediaBookmarks.likedList) ||
      pendingOperations.current["like"]
    )
      return;

    const likedIds = tatvaPediaBookmarks.likedList
      .filter((item: any) => item && item.contentId)
      .map((item: any) => item.contentId);

    setLocalLikes(likedIds);
  }, [tatvaPediaBookmarks.likedList]);

  useEffect(() => {
    if (
      !tatvaPediaBookmarks?.bookmarkedList ||
      !Array.isArray(tatvaPediaBookmarks.bookmarkedList)
    )
      return;

    const bookmarkedIds = tatvaPediaBookmarks.bookmarkedList
      .filter((item: any) => item && (item.contentId || item.id))
      .map((item: any) => item.contentId || item.id);

    setLocalBookmarks(bookmarkedIds);
  }, [tatvaPediaBookmarks?.bookmarkedList]);

  // Add effect for content counts
  useEffect(() => {
    const fetchContentCounts = async () => {
      const authToken = getToken();
      if (!authToken || items.length === 0) return;

      try {
        // Create a sorted string of content IDs for comparison
        const currentContentIds = items
          .map((doc: any) => `${doc.docId}_${doc.contentTypeId}`)
          .sort()
          .join(",");

        // Check if we've recently fetched the same content
        const now = Date.now();
        const timeSinceLastFetch = now - lastFetchRef.current.time;
        const isSameContent =
          currentContentIds === lastFetchRef.current.contentIds.join(",");

        // Only fetch if:
        // 1. It's been more than 30 seconds since last fetch, or
        // 2. The content items have changed
        if (timeSinceLastFetch < 30000 && isSameContent) {
          return;
        }

        const contents = items.map((doc: any) => ({
          contentId: doc.docId,
          contentTypeId: doc.contentTypeId,
        }));

        const countsResponse = await getContentCounts({
          contents,
          token: authToken,
          userId: doctorDetails?.user?.id,
        });

        if (countsResponse?.body) {
          const countsMap = countsResponse.body.reduce(
            (acc: any, item: any) => {
              const key = `${item.docId}_${item.docTypeId}`;
              acc[key] = {
                like_count: item.likeCount,
                isExternalSharable: item.isExternalSharable,
                uploadThumbnailUrl: item.uploadThumbnailUrl,
                previewUrl: item.previewUrl,
                readTime: item.readTime,
                duration: item.duration,
              };
              return acc;
            },
            {}
          );

          setContentCounts((prevCounts) => ({
            ...prevCounts,
            ...countsMap,
          }));

          // Update the last fetch reference
          lastFetchRef.current = {
            time: now,
            contentIds: currentContentIds.split(","),
          };
        }
      } catch (error) {
        console.error("Error fetching content counts:", error);
      }
    };

    fetchContentCounts();
  }, [items]);

  const handleLike = async (content: any, isLiked: boolean) => {
    if (!content?.docId) return;

    try {
      const token = getToken();
      if (!token) return;

      setIsActionLoading(true);
      // Set pending operation
      pendingOperations.current["like"] = true;

      // Optimistically update local state
      setLocalLikes((prev) => {
        const newLikes = isLiked
          ? prev.filter((id) => id !== content.docId)
          : [...prev, content.docId];

        return newLikes;
      });

      // Update content counts
      setContentCounts((prev) => {
        const key = `${content.docId}_${content.contentTypeId}`;
        const currentCount = prev[key] || { like_count: 0 };
        return {
          ...prev,
          [key]: {
            ...currentCount,
            like_count: currentCount.like_count + (isLiked ? -1 : 1),
          },
        };
      });

      // Make API call
      const response = await likeContent(
        content.docId,
        content.contentTypeId,
        !isLiked,
        token
      );

      if (response) {
        // Update global state after successful API call
        updateTatvaPediaLike(content, !isLiked);

        // Fetch updated like list to ensure consistency
        const updatedLikedContent = await getLikeBookmarkedContent(
          "liked",
          token
        );
        if (updatedLikedContent) {
          setTatvaPediaLikedList(updatedLikedContent);
        }
      } else {
        // Revert local states on failure
        setLocalLikes((prev) =>
          isLiked
            ? [...prev, content.docId]
            : prev.filter((id) => id !== content.docId)
        );

        setContentCounts((prev) => {
          const key = `${content.docId}_${content.contentTypeId}`;
          const currentCount = prev[key] || { like_count: 0 };
          return {
            ...prev,
            [key]: {
              ...currentCount,
              like_count: currentCount.like_count + (isLiked ? 1 : -1),
            },
          };
        });
      }
    } catch (error) {
      console.error("Error in like operation:", error);
      // Revert local states on error
      setLocalLikes((prev) =>
        isLiked
          ? [...prev, content.docId]
          : prev.filter((id) => id !== content.docId)
      );

      setContentCounts((prev) => {
        const key = `${content.docId}_${content.contentTypeId}`;
        const currentCount = prev[key] || { like_count: 0 };
        return {
          ...prev,
          [key]: {
            ...currentCount,
            like_count: currentCount.like_count + (isLiked ? 1 : -1),
          },
        };
      });
    } finally {
      setIsActionLoading(false);
      setTimeout(() => {
        pendingOperations.current["like"] = false;
      }, 1500);
    }
  };

  const handleBookmark = async (content: any, isBookmarked: boolean) => {
    if (!content?.docId) return;

    try {
      const token = getToken();
      if (!token) return;

      setIsActionLoading(true);
      pendingOperations.current["bookmark"] = true;

      // Optimistically update local state
      setLocalBookmarks((prev) => {
        const newBookmarks = isBookmarked
          ? prev.filter((id) => id !== content.docId)
          : [...prev, content.docId];
        return newBookmarks;
      });

      // Make API call
      const response = await bookmarkContent(
        content.docId,
        content.contentTypeId,
        !isBookmarked,
        token
      );

      if (!response) {
        // Revert on failure
        setLocalBookmarks((prev) =>
          isBookmarked
            ? [...prev, content.docId]
            : prev.filter((id) => id !== content.docId)
        );
        return;
      }

      // Update global state with complete content object
      await updateTatvaPediaBookmark(
        {
          ...content,
          id: content.docId,
          contentId: content.docId,
        },
        !isBookmarked
      );
    } catch (error) {
      console.error("Error in bookmark operation:", error);
      // Revert on error
      setLocalBookmarks((prev) =>
        isBookmarked
          ? [...prev, content.docId]
          : prev.filter((id) => id !== content.docId)
      );
    } finally {
      setTimeout(() => {
        setIsActionLoading(false);
        pendingOperations.current["bookmark"] = false;
      }, 1500);
    }
  };

  const isItemLiked = (item: ContentItem) => {
    if (!item?.docId) return false;
    const isLiked = localLikes.includes(item.docId);
    return isLiked;
  };

  const isItemBookmarked = (item: ContentItem) => {
    if (!item?.docId) return false;
    return (
      localBookmarks.includes(item.docId) ||
      (Array.isArray(tatvaPediaBookmarks?.bookmarkedList) &&
        tatvaPediaBookmarks.bookmarkedList.some(
          (bookmark: any) =>
            bookmark.contentId === item.docId || bookmark.id === item.docId
        ))
    );
  };

  // Helper function to get token
  const getToken = () =>
    tatvaPediaToken || localStorage.getItem("tatvaPediaToken");

  // Render empty state component
  const renderEmptyState = () => (
    <div className="bg-gray-50 rounded-xl p-6 my-4 text-center">
      <h3 className="text-gray-500 text-lg font-medium">
        No more latest content available in the selected speciality. Change the specialty to view more.
      </h3>
    </div>
  );

  // Render loading skeleton for sections
  const renderLoadingSkeleton = (count = 3) => (
    <div className="space-y-4">
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          className="animate-pulse bg-gray-200 rounded-xl h-32 w-full"
        ></div>
      ))}
    </div>
  );

  return (
    <div className="px-4 mt-4">

      {(loading || isActionLoading) && (
        <div className="fixed inset-0 bg-black bg-opacity-30 z-50 flex justify-center items-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
        </div>
      )}
      {items.length > 0 ? (
        <div className="space-y-4">
          {items.map((item: ContentItem) => {
            const countKey = `${item.docId}_${item.contentTypeId}`;
            const currentCounts = contentCounts[countKey] || {};

            return (
              <TpRectangleCard
                key={item.id}
                isShowcontentType={true}
                isLike={isItemLiked(item)}
                isBookmarked={isItemBookmarked(item)}
                onLike={() => handleLike(item, isItemLiked(item))}
                onBookmark={() => handleBookmark(item, isItemBookmarked(item))}
                data={{
                  docId: item.docId,
                  contentTitle: item.contentTitle,
                  contentType: item.contentType,
                  uploadThumbnailUrl: item.uploadThumbnailUrl,
                  contentDescription: item.contentDescription,
                  previewDeepLink: item.previewDeepLink,
                  like_count: currentCounts.like_count || item.like_count || 0,
                  contentTypeId: item.contentTypeId,
                  publishedDate: item.publishedDate || "",
                  isExternalSharable: currentCounts.isExternalSharable || 0,
                  previewUrl: currentCounts.previewUrl || "",
                  readTime:
                    contentCounts[`${item.docId}_${item.contentTypeId}`]
                      ?.readTime || "",
                  duration:
                    contentCounts[`${item.docId}_${item.contentTypeId}`]
                      ?.duration || "",
                  deepLinkCode: item.deepLinkCode,
                  consolidatedModule: item.consolidatedModule || "",
                }}
              />
            );
          })}
        </div>
      ) : (
        showEmptyState && renderEmptyState()
      )}
    </div>
  );
};

export default LatestSection;
